import { getTimerContext } from "@/components/nav/timer-context";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { useRowMutation } from "@/lib/use-api-mutation";
import { useDraft } from "@/lib/use-draft";
import { useRouter } from "@tanstack/react-router";
import { ExternalLink, Lightbulb, Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * In-place "New Idea" modal. Port of the Next-side
 * `src/components/ideas/new-idea-dialog.tsx`.
 *
 * Two save buttons mirror the Next behaviour:
 *   • "Save"               — POST + close + toast + invalidate.
 *   • "Save & open detail" — POST + navigate to the new idea.
 *
 * `defaultProjectId` lets callers pre-pick a project; absent
 * that, the page-level timer context supplies one.
 */

type Project = { id: string; name: string };

type Props = {
  children: React.ReactNode;
  defaultProjectId?: string;
};

let projectsPromise: Promise<Project[]> | null = null;
function loadProjects(): Promise<Project[]> {
  if (!projectsPromise) {
    projectsPromise = api
      // pageSize=200 mirrors the dialog's "I want every project I can see"
      // need without paginating; member-scoping is done server-side.
      .get<{ projects: Project[] }>("/api/projects?pageSize=200")
      .then((res) => res.projects)
      .catch(() => {
        projectsPromise = null;
        return [];
      });
  }
  return projectsPromise;
}

export function NewIdeaDialog({ children, defaultProjectId }: Props) {
  const { t } = useTranslation();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [title, setTitle] = useDraft(`new-idea:${defaultProjectId ?? "any"}:title`);
  const [description, setDescription] = useDraft(
    `new-idea:${defaultProjectId ?? "any"}:description`,
  );
  const [projectId, setProjectId] = useState(defaultProjectId ?? "");
  const create = useRowMutation(
    (_mode: "save" | "open") =>
      api.post<{ id: string }>("/api/ideas", {
        title: title.trim(),
        description: description.trim() || null,
        projectId: projectId || null,
      }),
    {
      rowKey: (mode) => mode,
      onSuccess: (idea, mode) => {
        setTitle("");
        setDescription("");
        if (mode === "open") {
          setOpen(false);
          // /ideas/:id isn't ported yet; full nav lands on the Next side.
          window.location.href = `/ideas/${idea.id}`;
        } else {
          toast({ title: t("idea.dialog.saved"), variant: "success" });
          setOpen(false);
          router.invalidate();
        }
      },
      onError: (msg) =>
        toast({ title: t("idea.dialog.saveFailed"), description: msg, variant: "destructive" }),
    },
  );

  useEffect(() => {
    if (!open) return;
    setLoadingProjects(projects.length === 0);
    loadProjects()
      .then(setProjects)
      .finally(() => setLoadingProjects(false));
  }, [open, projects.length]);

  useEffect(() => {
    if (open) {
      // Project precedence: explicit prop, then the page-level timer
      // context (set by SetTimerContext on project / WI pages).
      setProjectId(defaultProjectId ?? getTimerContext().projectId ?? "");
    }
  }, [open, defaultProjectId]);

  function submit(mode: "save" | "open") {
    if (!title.trim()) {
      toast({ title: t("idea.dialog.titleRequired"), variant: "destructive" });
      return;
    }
    create.run(mode);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      {/* Disable backdrop-click dismiss — the form holds typed
          input that an off-target click would discard. Close
          still works via the X (top-right) and the Cancel button
          in the footer. */}
      <DialogContent
        className="max-h-[90vh] max-w-xl overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-primary" />
            {t("idea.dialog.title")}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit("save");
          }}
          className="space-y-4"
        >
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">
              {t("idea.dialog.ideaTitle")} <span className="text-destructive">*</span>
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("idea.dialog.titlePlaceholder")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>

          <div className="block space-y-1.5">
            <span className="text-sm font-medium">
              {t("idea.dialog.description")}{" "}
              <span className="text-xs text-muted-foreground">{t("idea.dialog.markdown")}</span>
            </span>
            <MarkdownEditor
              value={description}
              onChange={setDescription}
              rows={6}
              autoGrow
              autoGrowMaxHeight="70vh"
              placeholder={t("idea.dialog.descriptionPlaceholder")}
              uploadEndpoint="/api/uploads"
            />
          </div>

          <div className="block space-y-1.5">
            <span className="text-sm font-medium">
              {t("idea.dialog.project")}{" "}
              <span className="text-xs text-muted-foreground">{t("idea.dialog.optional")}</span>
            </span>
            <Combobox
              value={projectId}
              onChange={setProjectId}
              loading={loadingProjects && projects.length === 0}
              disabled={loadingProjects && projects.length === 0}
              placeholder={t("idea.dialog.noProject")}
              emptyLabel={t("idea.dialog.noProjects")}
              clearable
              aria-label={t("idea.dialog.project")}
              options={projects.map((p) => ({ value: p.id, label: p.name }))}
            />
          </div>
        </form>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={create.busy}>
            {t("idea.dialog.cancel")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => submit("open")}
            disabled={create.busy}
            className="gap-1.5"
          >
            {create.isRunning("open") ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ExternalLink className="h-3.5 w-3.5" />
            )}
            {t("idea.dialog.saveOpen")}
          </Button>
          <Button
            size="sm"
            onClick={() => submit("save")}
            disabled={create.busy}
            className="gap-1.5"
          >
            {create.isRunning("save") ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            {t("idea.dialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
